import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { AuthAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Lien invalide. Veuillez recommencer la procédure.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return; }
    if (pwd !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    setBusy(true);
    try {
      await AuthAPI.resetPassword(token, pwd);
      setDone(true);
      setTimeout(() => navigate('/connexion'), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Échec de la réinitialisation.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] bg-[#FAF7F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white border border-[#E8E0D0] rounded-2xl p-7 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#1F4D3F] flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="font-serif text-2xl font-semibold text-[#1F4D3F]">Nouveau mot de passe</h1>
            <p className="text-xs text-[#7A6F5C]">Choisissez un mot de passe sécurisé pour votre compte Comptoir-Online</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="reset-password-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-medium text-[#2D2A26] mb-1">Mot de passe mis à jour</h2>
            <p className="text-sm text-[#7A6F5C] mb-4">Vous allez être redirigé vers la connexion…</p>
            <Link to="/connexion" className="text-[#1F4D3F] text-sm hover:underline">Aller à la connexion maintenant</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Nouveau mot de passe</label>
              <div className="relative">
                <Input type={show ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="Min. 6 caractères" required minLength={6} className="pr-10"
                  data-testid="reset-password-new" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A6F5C] hover:text-[#1F4D3F]" aria-label="Afficher/masquer">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Confirmer</label>
              <Input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Retapez le mot de passe" required data-testid="reset-password-confirm" />
            </div>
            {error && (
              <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-password-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" disabled={busy || !token} className="w-full bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="reset-password-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Mettre à jour
            </Button>
            <div className="text-center pt-2">
              <Link to="/connexion" className="text-xs text-[#7A6F5C] hover:text-[#1F4D3F]">← Retour à la connexion</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
